import React from 'react';
import styled from 'styled-components/macro';
import { useTranslate } from '../contexts/languageContext';
import NavMenu from '../components/NavMenu';
import Breadcrumbs from '../components/Breadcrumbs';
import { getUrl, getParents } from '../utils';
import { FiChevronRight } from 'react-icons/fi';
import { themes } from '../styles/themes';
import LinkWithTop from '../components/Links/LinkWithTop';
import { useMesa } from '../contexts/viewContext';
/**
 * Componente situado debajo del header que nos muestra información relevante en
 * cada página, como un menú de navegación por ámbitos o la hora del envío
 */

const SLowerBanner = styled.div`
  width: 100%;
  height: 33px;
  background-color: transparent;
  display: flex;
  align-items: center;
  margin-top: 0px;
  border-right: 0px solid #00f7f0;
  color: #164192;
  @media print {
    color: #164192;
    margin-top: 30px;
    display: none;
  }
`;

const SNavMenu = styled(NavMenu)`
  flex: 0 0 auto;
  vertical-align: middle;
  color: #1c4183;
  & button:focus {
    text-decoration: underline;
    text-decoration-color: currentColor;
  }
  & button:focus-visible {
    outline: none;
  }
`;

const LowerBanner = ({
  nomenclator,
  eleccion,
  ambito,
  page,
  variant,
  esHistorico,
  data,
  parentLevel,
}) => {
  const t = useTranslate();
  const esBoletin = data.tope === '1';
  return (
    <SLowerBanner>
      <StyledRoutedBreadcrumbs
        eleccion={eleccion}
        ambito={ambito}
        nomenclator={nomenclator}
        page={page}
        esHistorico={esHistorico}
        parentLevel={parentLevel}
        variant={variant}
      />
      {esHistorico &&
        (esBoletin ? (
          <Anterior>({t('boletinAnterior')})</Anterior>
        ) : (
          <Anterior>({t('avanceAnterior')})</Anterior>
        ))}
    </SLowerBanner>
  );
};

export default LowerBanner;

const RoutedBreadcrumbs = ({
  eleccion,
  ambito,
  nomenclator,
  className,
  page,
  esHistorico,
  parentLevel,
  variant,
}) => {
  const parents = getParents(
    ambito.i,
    eleccion,
    nomenclator,
    parentLevel
  ).reverse();
  const { mesa, setMesa } = useMesa();
  let ids = [];
  let routes = [];
  if (mesa) {
    ids = [...parents, ambito.i].slice(-2);
    routes[0] = {
      url: getUrl({ id: ambito.i, eleccion, nomenclator, page, parentLevel }),
      n: nomenclator.ambitos[eleccion][ambito.i].n,
    };
    routes[1] = {
      url: getUrl({ id: ambito.i, eleccion, nomenclator, page, parentLevel }),
      n: 'Mesa ' + mesa.slice(-6),
    };
  } else {
    ids = [...parents, ambito.i].slice(-2);
    routes = ids.map(id => ({
      url: getUrl({ id, eleccion, nomenclator, page, parentLevel }),
      n: nomenclator.ambitos[eleccion][id].n,
    }));
  }

  return (
    <Breadcrumbs
      aria-label="migas"
      separator={<FiChevronRight />}
      className={className}
      esHistorico={esHistorico}
    >
      {routes.map(({ url, n }, index) => {
        const last = index === routes.length - 1;
        return (
          <div className="texto" key={url}>
            {last ? (
              <h2 data-testid="last-breadcrumb">
                <SNavMenu
                  nomenclator={nomenclator}
                  eleccion={eleccion}
                  ambito={ambito}
                  page={page}
                  parentLevel={parentLevel}
                >
                  {n}
                </SNavMenu>
              </h2>
            ) : esHistorico ? (
              <LinkBreadCrumb className="firstLabel" to={url}>
                <span
                  className={last ? 'last' : 'first-historico'}
                  data-testid={last ? 'last-breadcrumb' : undefined}
                >
                  {n} /
                </span>
              </LinkBreadCrumb>
            ) : (
              <LinkBreadCrumb
                className="firstLabel"
                to={url}
                onClick={() => setMesa('')}
              >
                <LinkSpan className="link-span">{n}</LinkSpan>
                <BarraLink>{' / '}</BarraLink>
              </LinkBreadCrumb>
            )}
          </div>
        );
      })}
    </Breadcrumbs>
  );
};

const StyledRoutedBreadcrumbs = styled(RoutedBreadcrumbs)`
  box-sizing: border-box;
  background-color: #fff;
  color: #000;
  .breadcrumbs {
    display: flex;
  }
  .breadcrumbs-item {
    vertical-align: middle;
    font-weight: 100;
    min-width: 44px;
    height: 33px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .first a {
    width: 44px;
    height: 33px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .texto {
    font-size: 14px;
    padding: 0px;
  }
  .texto > .first {
    color: #000;
    font-size: 16px;
    font-size: 30px;
  }
  .texto > .firstLabel {
    color: transparent;
  }

  .texto > .first-historico {
    color: #000;
    font-size: 16px;
    font-weight: 100;
    margin-left: 20px;
  }

  .texto > .last {
    color: #000;
    font-size: 16px;
    font-weight: 700;
  }

  .breadcrumbs-separator {
    color: #000;
    font-size: 16px;
    display: inline-block;
    vertical-align: middle;
  }

  @media print {
    display: none;
  }
`;

const Anterior = styled.p`
  margin-left: 20px;
  font-size: 30px;
  color: #000;
  @media (max-width: 1024px) {
    font-size: 16px;
    margin-left: 5px;
  }
`;

const LinkBreadCrumb = styled(LinkWithTop)`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: auto;
  color: ${themes.inicio.base};
  :focus {
    & .link-span {
      text-decoration: underline;
      text-decoration-color: currentColor;
    }
  }
`;

const LinkSpan = styled.span`
  box-sizing: border-box;
  display: flex;
  cursor: pointer;
  color: ${themes.inicio.base};
  font-weight: 700;
  font-size: 3.2rem;
  &:hover {
    text-decoration: none;
    scale: 1.05;
  }
  @media (max-width: 1400px) {
    font-size: 1.2rem;
  }
`;

const BarraLink = styled.span`
  box-sizing: border-box;
  color: ${themes.inicio.base};
  font-weight: 700;
  font-size: 3.2rem;
  margin-left: 1.5rem;
  margin-right: 1.5rem;
  @media (max-width: 1400px) {
    font-size: 1.2rem;
  }
`;
